import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import classes from "./styles/SeoJobDescription.module.css";
import { getJobById } from "../../../services/seoPageService";

// ── Helpers ──────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatDateISO = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toISOString();
};

const buildJobPostingSchema = (detail, jobId, country) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": detail.title || "",
    "description": detail.description || detail.summary || "",
    "datePosted": formatDateISO(detail.datePosted),
    "hiringOrganization": {
      "@type": "Organization",
      "name": detail.company || "",
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        ...(detail.geoLocation?.city && { "addressLocality": detail.geoLocation.city }),
        ...(detail.geoLocation?.state && { "addressRegion": detail.geoLocation.state }),
        ...(detail.geoLocation?.country && { "addressCountry": detail.geoLocation.country }),
        ...(detail.geoLocation?.zipCode && { "postalCode": detail.geoLocation.zipCode }),
      },
    },
    ...(detail.jobType && {
      "employmentType": detail.jobType.toUpperCase().replace(/\s+/g, "_"),
    }),
    ...(detail.workLocation === "Remote" && {
      "jobLocationType": "TELECOMMUTE",
    }),
    ...(detail.salary && { "baseSalary": { "@type": "MonetaryAmount", "description": detail.salary } }),
    ...(detail.expired === false && { "validThrough": "" }),
    "url":  `https://www.fetchjobs.co/job/description/${jobId}?country=${country}`,
  };
  return schema;
};

const buildBreadcrumbSchema = (detail, jobId, country) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.fetchjobs.co" },
    { "@type": "ListItem", "position": 2, "name": "Jobs", "item": "https://www.fetchjobs.co/jobs" },
    {
      "@type": "ListItem",
      "position": 3,
      "name": detail.title || "Job Description",
      "item": `https://www.fetchjobs.co/job/description/${jobId}?country=${country}`,
    },
  ],
});

// ── Skeleton ─────────────────────────────────────────────────
const DescriptionPlaceholder = () => (
  <div className={classes.placeholderWrap}>
    <div className={classes.phBox}>
      <div className={classes.phTitle} />
      <div className={classes.phLine} />
      <div className={classes.phLine} style={{ width: "55%" }} />
    </div>
    <div className={classes.phBody}>
      {[100, 85, 90, 70, 95, 75].map((w, i) => (
        <div key={i} className={classes.phLine} style={{ width: `${w}%` }} />
      ))}
    </div>
  </div>
);

// ── Component ─────────────────────────────────────────────────
const SeoJobDescription = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const country = searchParams.get("country") || "";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    getJobById(jobId, country)
      .then((data) => { setDetail(data); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [jobId, country]);

  // ── Derived meta values ──
  const pageTitle   = detail?.title ? `${detail.title} at ${detail.company || "Top Company"} | FetchJobs` : "Job Description – FetchJobs";
  const metaDesc    = detail
    ? `Apply for ${detail.title} at ${detail.company || "a top company"}${detail.geoLocation?.city ? ` in ${detail.geoLocation.city}` : ""}. View full job description, requirements and apply now on FetchJobs.`
    : "View job description and apply now on FetchJobs.";
  const canonicalUrl = `https://www.fetchjobs.co/job/description/${jobId}?country=${country}`;

  return (
    <div className={classes.jobdesCon}>

      {/* ── Technical SEO ── */}
      <Helmet>
        {/* Core */}
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="robots" content={detail?.expired ? "noindex, nofollow" : "index, follow"} />

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url"         content={canonicalUrl} />
        <meta property="og:site_name"   content="FetchJobs" />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={metaDesc} />

        {/* JobPosting schema — enables Google for Jobs */}
        {detail && (
          <script type="application/ld+json">
            {JSON.stringify(buildJobPostingSchema(detail, jobId, country))}
          </script>
        )}

        {/* Breadcrumb schema */}
        {detail && (
          <script type="application/ld+json">
            {JSON.stringify(buildBreadcrumbSchema(detail, jobId, country))}
          </script>
        )}
      </Helmet>

      <h1 className={classes.title}>About the Job</h1>
      <p className={classes.subTitle}>Everything you need to know before applying for this role.</p>

      {loading && <DescriptionPlaceholder />}

      {!loading && error && (
        <div className={classes.errorBox}>
          <p>Could not load job details. Please try again later.</p>
        </div>
      )}

      {!loading && !error && detail && (
        <div className={classes.container}>
          {/* ── Upper card ── */}
          <div className={classes.upperCon}>
            <div>
              <h2 className={classes.jobTitle}>{detail.title}</h2>
              <div className={classes.companyCon}>
                {detail.company && (
                  <p>
                    <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2"/>
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                    </svg>
                    {detail.company}
                  </p>
                )}
                {detail.geoLocation && (
                  <p>
                    <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {[detail.geoLocation.city, detail.geoLocation.state, detail.geoLocation.country]
                      .filter(Boolean).join(", ")}
                  </p>
                )}
                {detail.datePosted && (
                  <p>
                    <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {formatDate(detail.datePosted)}
                  </p>
                )}
                {detail.jobType && (
                  <p>
                    <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    {detail.jobType}
                  </p>
                )}
                {detail.workLocation && (
                  <p>
                    <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2"/>
                      <path d="M8 21h8M12 17v4"/>
                    </svg>
                    {detail.workLocation}
                  </p>
                )}
              </div>
            </div>

            <div className={classes.applyBtnCon}>
              {detail.uri && (
                <a
                  href={detail.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.applyBtn}
                >
                  Apply Now
                </a>
              )}
            </div>
          </div>

          {/* ── Description card ── */}
          <div className={classes.desCon}>
            {detail.descriptionHTML ? (
              <div dangerouslySetInnerHTML={{ __html: detail.descriptionHTML }} />
            ) : detail.description ? (
              <div style={{ whiteSpace: "pre-line" }}>{detail.description}</div>
            ) : detail.summary ? (
              <div dangerouslySetInnerHTML={{ __html: detail.summary }} />
            ) : (
              <p style={{ color: "#64748b" }}>No description available for this job.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoJobDescription;
